/* eslint-disable no-shadow */
import React from 'react';
import { bool, func, shape, string, node, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';

import {
  Form,
  PrimaryButton,
  EditListingActions,
  EditListingFieldWrapper,
  EditListingSection,
  EditListingSectionHeading,
  FieldTextInput,
  FieldToggle,
  FieldCondition,
  FormValidationTooltip,
} from '../../components';
import { requestImageUpload as requestImageUploadAction } from '../../containers/EditListingPage/EditListingPage.duck';

import css from './EditListingFarmExperienceForm.module.css';

const EditListingFarmExperienceFormComponent = ({ backLink, listing, onUpdate, ...formProps }) => {
  return (
    <FinalForm
      {...formProps}
      keepDirtyOnReinitialize
      mutators={{ ...arrayMutators }}
      render={fieldRenderProps => {
        const {
          className,
          disabled,
          handleSubmit,
          intl,
          invalid,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress,
          packageSection,
          errors,
          packages,
        } = fieldRenderProps;

        const classes = classNames(css.root, className);
        const submitReady = updated && pristine;
        const submitInProgress = updateInProgress;
        const submitDisabled =
          invalid || disabled || submitInProgress || (packages && !packages.length);
        const allErrors = {
          ...errors,
        };

        if (packages && !packages.length) {
          allErrors.packages = intl.formatMessage({
            id: 'EditListingHuntExperienceForm.packagesRequired',
          });
        }

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true }}
              onChange={props => {
                onUpdate(props.values);
              }}
            />

            <EditListingSection
              hasBackground
              title={intl.formatMessage({
                id: 'EditListingFarmExperienceForm.activeWorkingRanchLabel',
              })}
              hasBottomMargin
            >
              <EditListingFieldWrapper>
                <FieldToggle name="hasActiveWorkingRanch" />
              </EditListingFieldWrapper>
              <FieldCondition when="hasActiveWorkingRanch" is>
                <EditListingFieldWrapper>
                  <FieldTextInput
                    id="activeWorkingRanchInfo"
                    name="activeWorkingRanchInfo"
                    type="textarea"
                    label={intl.formatMessage({
                      id: 'EditListingFarmExperienceForm.activeWorkingRanchInfo',
                    })}
                  />
                </EditListingFieldWrapper>
              </FieldCondition>
            </EditListingSection>

            <EditListingSection>
              <EditListingSectionHeading>
                <FormattedMessage id="EditListingFarmExperienceForm.amenitiesLabel" />
              </EditListingSectionHeading>
            </EditListingSection>

            <EditListingSection
              hasBackground
              hasBottomMargin
              title={intl.formatMessage({ id: 'EditListingFarmExperienceForm.cateringLabel' })}
            >
              <EditListingFieldWrapper>
                <FieldToggle name="hasCatering" />
              </EditListingFieldWrapper>

              <FieldCondition when="hasCatering" is>
                <EditListingFieldWrapper>
                  <FieldTextInput
                    id="cateringInfo"
                    name="cateringInfo"
                    type="textarea"
                    label={intl.formatMessage({
                      id: 'EditListingFarmExperienceForm.cateringInfo',
                    })}
                  />
                </EditListingFieldWrapper>
              </FieldCondition>
            </EditListingSection>

            <EditListingSection
              hasBackground
              title={intl.formatMessage({ id: 'EditListingFarmExperienceForm.swimmingLabel' })}
              hasBottomMargin
            >
              <EditListingFieldWrapper>
                <FieldToggle name="hasSwimming" />
              </EditListingFieldWrapper>

              <FieldCondition when="hasSwimming" is>
                <EditListingFieldWrapper>
                  <FieldTextInput
                    id="swimmingInfo"
                    name="swimmingInfo"
                    type="textarea"
                    label={intl.formatMessage({
                      id: 'EditListingFarmExperienceForm.swimmingInfo',
                    })}
                  />
                </EditListingFieldWrapper>
              </FieldCondition>
            </EditListingSection>

            <EditListingSection
              hasBackground
              title={intl.formatMessage({ id: 'EditListingFarmExperienceForm.atvLabel' })}
              hasBottomMargin
            >
              <EditListingFieldWrapper>
                <FieldToggle name="hasAtv" />
              </EditListingFieldWrapper>

              <FieldCondition when="hasAtv" is>
                <EditListingFieldWrapper>
                  <FieldTextInput
                    id="atvInfo"
                    name="atvInfo"
                    type="textarea"
                    label={intl.formatMessage({
                      id: 'EditListingFarmExperienceForm.atvInfo',
                    })}
                  />
                </EditListingFieldWrapper>
              </FieldCondition>
            </EditListingSection>

            {packageSection}

            <EditListingActions>
              {backLink && <EditListingActions.Back>{backLink}</EditListingActions.Back>}

              <EditListingActions.Forward>
                {Object.keys(allErrors).length > 0 ? (
                  <FormValidationTooltip
                    placement="top"
                    body={
                      <ul>
                        {Object.keys(allErrors).map(field => (
                          <li key={`tooltip-field-${field}`}>{allErrors[field]}</li>
                        ))}
                      </ul>
                    }
                    title={
                      <FormattedMessage id="EditListingHuntExperienceForm.validationTooltipTitle" />
                    }
                  >
                    <PrimaryButton type="button" disabled>
                      {saveActionMsg}
                    </PrimaryButton>
                  </FormValidationTooltip>
                ) : (
                  <PrimaryButton
                    type="submit"
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                    ready={submitReady}
                  >
                    {saveActionMsg}
                  </PrimaryButton>
                )}
              </EditListingActions.Forward>
            </EditListingActions>
          </Form>
        );
      }}
    />
  );
};

EditListingFarmExperienceFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  backLink: null,
};

EditListingFarmExperienceFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  backLink: node,
  listing: object.isRequired,
  packageSection: node.isRequired,
};

const mapDispatchToProps = dispatch => ({
  requestImageUpload: (file, onUploadProgress) =>
    dispatch(requestImageUploadAction({ file, onUploadProgress })),
});

export default compose(
  connect(
    null,
    mapDispatchToProps
  )
)(injectIntl(EditListingFarmExperienceFormComponent));
